import axios from 'axios';
import { authRequestInterceptor } from '../../services/authInterceptor';
import {
  Style,
  SkillLevel,
  Instructor,
  Move,
  MoveType,
  HandHold,
  Position,
  Dancer,
  Demonstration,
  DemonstrationType,
  Pattern,
  CreatePatternData,
  ApiResponse,
  DashboardStats,
  CreateStyleData,
  CreateSkillLevelData,
  CreateMoveData,
  CreateMoveTypeData,
  CreateHandHoldData,
  CreatePositionData,
  Member,
  MemberRole,
  UpdateInstructorProfileData,
  UpdateDancerProfileData,
  DanceRole,
  BreakdownAngle,
  BreakdownClipType,
  MoveBreakdown,
  CreateDanceRoleData,
  CreateBreakdownAngleData,
  CreateBreakdownClipTypeData,
  CreateMoveBreakdownData,
  TenantInvite,
  CreateInviteData,
  CreateInviteResponse,
  BillingOverview,
  PlanInput,
  MemberSubscription,
  GrantInput,
} from '../types';
import { getApiBaseUrl, tenantHeaders } from '../../config/apiBase';

export { getApiBaseUrl };

const API_BASE_URL = getApiBaseUrl();
const MEDIA_BASE_URL = API_BASE_URL.replace(/\/api$/, '');

export const getAdminMediaUrl = (path: string): string => {
  if (path.startsWith('http')) return path;
  return `${MEDIA_BASE_URL}${path}`;
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    ...tenantHeaders,
  },
});

api.interceptors.request.use(authRequestInterceptor);

// Dashboard API
export const dashboardApi = {
  getStats: (): Promise<DashboardStats> =>
    api.get('/admin/dashboard/stats').then((res) => res.data),

  getAnalytics: (period: string = '30d') =>
    api.get(`/admin/dashboard/analytics?period=${period}`).then((res) => res.data),
};

// Styles API
export const stylesApi = {
  getAll: (params?: { page?: number; limit?: number; search?: string }): Promise<ApiResponse<Style>> =>
    api.get('/admin/styles', { params }).then((res) => res.data),

  getById: (id: number): Promise<Style> =>
    api.get(`/admin/styles/${id}`).then((res) => res.data),

  create: (data: CreateStyleData): Promise<Style> =>
    api.post('/admin/styles', data).then((res) => res.data),

  update: (id: number, data: CreateStyleData): Promise<Style> =>
    api.put(`/admin/styles/${id}`, data).then((res) => res.data),

  delete: (id: number): Promise<void> =>
    api.delete(`/admin/styles/${id}`).then((res) => res.data),
};

// Skill Levels API
export const skillLevelsApi = {
  getAll: (params?: { page?: number; limit?: number; search?: string }): Promise<ApiResponse<SkillLevel>> =>
    api.get('/admin/skill-levels', { params }).then((res) => res.data),

  getById: (id: number): Promise<SkillLevel> =>
    api.get(`/admin/skill-levels/${id}`).then((res) => res.data),

  create: (data: CreateSkillLevelData): Promise<SkillLevel> =>
    api.post('/admin/skill-levels', data).then((res) => res.data),

  update: (id: number, data: CreateSkillLevelData): Promise<SkillLevel> =>
    api.put(`/admin/skill-levels/${id}`, data).then((res) => res.data),

  delete: (id: number): Promise<void> =>
    api.delete(`/admin/skill-levels/${id}`).then((res) => res.data),
};

// Move Types API
export const moveTypesApi = {
  getAll: (params?: { page?: number; limit?: number; search?: string }): Promise<ApiResponse<MoveType>> =>
    api.get('/admin/move-types', { params }).then((res) => res.data),

  getById: (id: number): Promise<MoveType> =>
    api.get(`/admin/move-types/${id}`).then((res) => res.data),

  create: (data: CreateMoveTypeData): Promise<MoveType> =>
    api.post('/admin/move-types', data).then((res) => res.data),

  update: (id: number, data: CreateMoveTypeData): Promise<MoveType> =>
    api.put(`/admin/move-types/${id}`, data).then((res) => res.data),

  delete: (id: number): Promise<void> =>
    api.delete(`/admin/move-types/${id}`).then((res) => res.data),
};

// Hand Holds API
export const handHoldsApi = {
  getAll: (params?: { page?: number; limit?: number; search?: string }): Promise<ApiResponse<HandHold>> =>
    api.get('/admin/hand-holds', { params }).then((res) => res.data),
  getById: (id: number): Promise<HandHold> =>
    api.get(`/admin/hand-holds/${id}`).then((res) => res.data),
  create: (data: CreateHandHoldData): Promise<HandHold> =>
    api.post('/admin/hand-holds', data).then((res) => res.data),
  update: (id: number, data: CreateHandHoldData): Promise<HandHold> =>
    api.put(`/admin/hand-holds/${id}`, data).then((res) => res.data),
  delete: (id: number): Promise<void> =>
    api.delete(`/admin/hand-holds/${id}`).then((res) => res.data),
};

// Positions API
export const positionsApi = {
  getAll: (params?: { page?: number; limit?: number; search?: string }): Promise<ApiResponse<Position>> =>
    api.get('/admin/positions', { params }).then((res) => res.data),
  getById: (id: number): Promise<Position> =>
    api.get(`/admin/positions/${id}`).then((res) => res.data),
  create: (data: CreatePositionData): Promise<Position> =>
    api.post('/admin/positions', data).then((res) => res.data),
  update: (id: number, data: CreatePositionData): Promise<Position> =>
    api.put(`/admin/positions/${id}`, data).then((res) => res.data),
  delete: (id: number): Promise<void> =>
    api.delete(`/admin/positions/${id}`).then((res) => res.data),
};

// Instructors API
export const instructorsApi = {
  getAll: (params?: { page?: number; limit?: number; search?: string; status?: string }): Promise<ApiResponse<Instructor>> =>
    api.get('/admin/instructors', { params }).then((res) => res.data),

  getById: (id: number): Promise<Instructor> =>
    api.get(`/admin/instructors/${id}`).then((res) => res.data),

  update: (id: number, data: UpdateInstructorProfileData): Promise<Instructor> =>
    api.put(`/admin/instructors/${id}`, data).then((res) => res.data),

  delete: (id: number): Promise<void> =>
    api.delete(`/admin/instructors/${id}`).then((res) => res.data),

  migrateMoves: (id: number, data: { targetInstructorId: number; moveIds?: number[] }): Promise<{ migratedCount: number; remainingCount: number }> =>
    api.post(`/admin/instructors/${id}/migrate-moves`, data).then((res) => res.data),

  migratePatterns: (id: number, data: { targetInstructorId: number; patternIds?: number[] }): Promise<{ migratedCount: number; remainingCount: number }> =>
    api.post(`/admin/instructors/${id}/migrate-patterns`, data).then((res) => res.data),
};


// Moves API
export const movesApi = {
  getAll: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    styleId?: number;
    skillLevelId?: number;
    instructorId?: number;
    moveTypeId?: number;
    handHoldId?: number;
    published?: boolean;
  }): Promise<ApiResponse<Move>> =>
    api.get('/admin/moves', { params }).then((res) => res.data),

  getById: (id: number): Promise<Move> =>
    api.get(`/admin/moves/${id}`).then((res) => res.data),

  create: (data: CreateMoveData): Promise<Move> =>
    api.post('/admin/moves', data).then((res) => res.data),

  update: (id: number, data: CreateMoveData): Promise<Move> =>
    api.put(`/admin/moves/${id}`, data).then((res) => res.data),

  delete: (id: number): Promise<void> =>
    api.delete(`/admin/moves/${id}`).then((res) => res.data),
};

// Dancers API
export const dancersApi = {
  getAll: (params?: { page?: number; limit?: number; search?: string; active?: boolean }): Promise<ApiResponse<Dancer>> =>
    api.get('/dancers', { params: { includeUnverified: true } }).then((res) => {
      let data: Dancer[] = res.data;
      if (params?.active !== undefined) {
        data = data.filter((d) => d.isActive === params.active);
      }
      if (params?.search) {
        const s = params.search.toLowerCase();
        data = data.filter((d) => d.name.toLowerCase().includes(s) || d.email.toLowerCase().includes(s));
      }
      return {
        data,
        pagination: { page: params?.page || 1, limit: params?.limit || 10, total: data.length, pages: 1 }
      };
    }),

  getById: (id: number): Promise<Dancer> =>
    api.get(`/dancers/${id}`).then((res) => res.data),

  update: (id: number, data: UpdateDancerProfileData): Promise<Dancer> =>
    api.put(`/dancers/${id}`, data).then((res) => res.data),

  activate: (id: number): Promise<Dancer> =>
    api.post(`/dancers/${id}/activate`).then((res) => res.data),

  deactivate: (id: number): Promise<Dancer> =>
    api.post(`/dancers/${id}/deactivate`).then((res) => res.data),

  delete: (id: number): Promise<void> =>
    api.delete(`/dancers/${id}`).then((res) => res.data),
};

// Demonstrations API
export const demonstrationsApi = {
  getAll: (params?: {
    page?: number;
    limit?: number;
    moveId?: number;
    patternId?: number;
    demonstrationType?: DemonstrationType;
    dancerId?: number;
    approved?: boolean | 'all';
    featured?: boolean;
  }): Promise<ApiResponse<Demonstration>> =>
    api.get('/demonstrations', { params }).then((res) => ({
      data: res.data,
      pagination: { page: params?.page || 1, limit: params?.limit || 10, total: res.data.length, pages: 1 }
    })),

  getPending: (): Promise<Demonstration[]> =>
    api.get('/demonstrations', { params: { approved: false } }).then((res) => res.data),

  getById: (id: number): Promise<Demonstration> =>
    api.get(`/demonstrations/${id}`).then((res) => res.data),

  getFeatured: (): Promise<Demonstration | null> =>
    api.get('/demonstrations/featured').then((res) => res.data),

  upload: (data: FormData): Promise<Demonstration> =>
    api.post('/demonstrations/upload', data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then((res) => res.data),

  adminUpload: (data: FormData): Promise<Demonstration> =>
    api.post('/demonstrations/admin/upload', data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then((res) => res.data),

  approve: (id: number): Promise<Demonstration> =>
    api.post(`/demonstrations/${id}/approve`).then((res) => res.data),

  reject: (id: number): Promise<Demonstration> =>
    api.post(`/demonstrations/${id}/reject`).then((res) => res.data),

  feature: (id: number): Promise<Demonstration> =>
    api.post(`/demonstrations/${id}/feature`).then((res) => res.data),

  unfeature: (id: number): Promise<Demonstration> =>
    api.post(`/demonstrations/${id}/unfeature`).then((res) => res.data),

  update: (id: number, data: { caption?: string; dancerId?: number; moveId?: number; patternId?: number }): Promise<Demonstration> =>
    api.put(`/demonstrations/${id}/admin`, data).then((res) => res.data),

  delete: (id: number): Promise<void> =>
    api.delete(`/demonstrations/${id}`).then((res) => res.data),
};

// Patterns API
export const patternsApi = {
  getAll: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    styleId?: number;
    skillLevelId?: number;
    instructorId?: number;
    published?: boolean;
  }): Promise<ApiResponse<Pattern>> =>
    api.get('/admin/patterns', { params }).then((res) => res.data),

  getById: (id: number): Promise<Pattern> =>
    api.get(`/admin/patterns/${id}`).then((res) => res.data),

  create: (data: CreatePatternData): Promise<Pattern> =>
    api.post('/admin/patterns', data).then((res) => res.data),

  update: (id: number, data: Partial<Omit<CreatePatternData, 'moves'>>): Promise<Pattern> =>
    api.put(`/admin/patterns/${id}`, data).then((res) => res.data),

  updateMoves: (id: number, moves: { moveId: number; position?: number; notes?: string | null }[]): Promise<Pattern> =>
    api.put(`/admin/patterns/${id}/moves`, { moves }).then((res) => res.data),

  delete: (id: number): Promise<void> =>
    api.delete(`/admin/patterns/${id}`).then((res) => res.data),
};

// Dance Roles API
export const danceRolesApi = {
  getAll: (params?: { page?: number; limit?: number; search?: string }): Promise<ApiResponse<DanceRole>> =>
    api.get('/admin/dance-roles', { params }).then((res) => res.data),
  getById: (id: number): Promise<DanceRole> =>
    api.get(`/admin/dance-roles/${id}`).then((res) => res.data),
  create: (data: CreateDanceRoleData): Promise<DanceRole> =>
    api.post('/admin/dance-roles', data).then((res) => res.data),
  update: (id: number, data: CreateDanceRoleData): Promise<DanceRole> =>
    api.put(`/admin/dance-roles/${id}`, data).then((res) => res.data),
  delete: (id: number): Promise<void> =>
    api.delete(`/admin/dance-roles/${id}`).then((res) => res.data),
};

// Breakdown Angles API
export const breakdownAnglesApi = {
  getAll: (params?: { page?: number; limit?: number; search?: string }): Promise<ApiResponse<BreakdownAngle>> =>
    api.get('/admin/breakdown-angles', { params }).then((res) => res.data),
  getById: (id: number): Promise<BreakdownAngle> =>
    api.get(`/admin/breakdown-angles/${id}`).then((res) => res.data),
  create: (data: CreateBreakdownAngleData): Promise<BreakdownAngle> =>
    api.post('/admin/breakdown-angles', data).then((res) => res.data),
  update: (id: number, data: CreateBreakdownAngleData): Promise<BreakdownAngle> =>
    api.put(`/admin/breakdown-angles/${id}`, data).then((res) => res.data),
  delete: (id: number): Promise<void> =>
    api.delete(`/admin/breakdown-angles/${id}`).then((res) => res.data),
};

// Breakdown Clip Types API
export const breakdownClipTypesApi = {
  getAll: (params?: { page?: number; limit?: number; search?: string }): Promise<ApiResponse<BreakdownClipType>> =>
    api.get('/admin/breakdown-clip-types', { params }).then((res) => res.data),
  getById: (id: number): Promise<BreakdownClipType> =>
    api.get(`/admin/breakdown-clip-types/${id}`).then((res) => res.data),
  create: (data: CreateBreakdownClipTypeData): Promise<BreakdownClipType> =>
    api.post('/admin/breakdown-clip-types', data).then((res) => res.data),
  update: (id: number, data: CreateBreakdownClipTypeData): Promise<BreakdownClipType> =>
    api.put(`/admin/breakdown-clip-types/${id}`, data).then((res) => res.data),
  delete: (id: number): Promise<void> =>
    api.delete(`/admin/breakdown-clip-types/${id}`).then((res) => res.data),
};

// Breakdowns API
export const breakdownsApi = {
  getAll: (params?: { page?: number; limit?: number; moveId?: number; search?: string }): Promise<ApiResponse<MoveBreakdown>> =>
    api.get('/admin/breakdowns', { params }).then((res) => res.data),
  create: (data: FormData): Promise<MoveBreakdown> =>
    api.post('/admin/breakdowns', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((res) => res.data),
  update: (id: number, data: Partial<CreateMoveBreakdownData>): Promise<MoveBreakdown> =>
    api.put(`/admin/breakdowns/${id}`, data).then((res) => res.data),
  delete: (id: number): Promise<void> =>
    api.delete(`/admin/breakdowns/${id}`).then((res) => res.data),
  reorder: (items: { id: number; order: number }[]): Promise<void> =>
    api.post('/admin/breakdowns/reorder', { items }).then((res) => res.data),
};

// Admin Videos API — wraps the auth interceptor so the Cognito JWT is attached.
// Previously these endpoints were called via native fetch(), which bypasses the
// axios interceptor and returns 401. See N-106.
export const adminVideosApi = {
  uploadVideo: (file: File): Promise<{ videoUrl: string; thumbnailUrl?: string }> => {
    const formData = new FormData();
    formData.append('video', file);
    return api.post('/admin/videos/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((res) => res.data);
  },

  uploadThumbnail: (file: File): Promise<{ thumbnailUrl: string }> => {
    const formData = new FormData();
    formData.append('thumbnail', file);
    return api.post('/admin/videos/thumbnail/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((res) => res.data);
  },
};

// Members (DB users with Memberships in the current tenant)
export const membersApi = {
  getAll: (): Promise<Member[]> =>
    api.get('/admin/members').then((res) => res.data),

  setRoles: (userId: number, roles: MemberRole[]): Promise<{ userId: number; roles: MemberRole[] }> =>
    api.put(`/admin/members/${userId}/roles`, { roles }).then((res) => res.data),

  remove: (userId: number): Promise<void> =>
    api.delete(`/admin/members/${userId}`).then(() => undefined),
};

// Tenant invites API
export const invitesApi = {
  getAll: (status: 'pending' | 'all' = 'pending'): Promise<TenantInvite[]> =>
    api.get('/admin/invites', { params: { status } }).then((res) => res.data),

  create: (data: CreateInviteData): Promise<CreateInviteResponse> =>
    api.post('/admin/invites', data).then((res) => res.data),

  delete: (id: number): Promise<void> =>
    api.delete(`/admin/invites/${id}`).then(() => undefined),
};

export default api;
// Paid memberships: Stripe connection, plans, subscribers, free grants
export const billingApi = {
  getOverview: (): Promise<BillingOverview> =>
    api.get('/admin/billing').then((res) => res.data),

  connectStripe: (): Promise<{ url: string }> =>
    api.post('/admin/billing/connect').then((res) => res.data),

  refreshStripe: (): Promise<{ connected: boolean; ready: boolean }> =>
    api.post('/admin/billing/refresh').then((res) => res.data),

  setMembershipsEnabled: (membershipsEnabled: boolean): Promise<{ membershipsEnabled: boolean }> =>
    api.put('/admin/billing/settings', { membershipsEnabled }).then((res) => res.data),

  createPlan: (data: PlanInput): Promise<{ id: number }> =>
    api.post('/admin/billing/plans', data).then((res) => res.data),

  updatePlan: (id: number, data: Partial<PlanInput>): Promise<{ id: number }> =>
    api.put(`/admin/billing/plans/${id}`, data).then((res) => res.data),

  getSubscriptions: (): Promise<MemberSubscription[]> =>
    api.get('/admin/billing/subscriptions').then((res) => res.data),

  grant: (data: GrantInput): Promise<{ id: number }> =>
    api.post('/admin/billing/grants', data).then((res) => res.data),

  revokeGrant: (id: number): Promise<void> =>
    api.delete(`/admin/billing/grants/${id}`).then(() => undefined),
};
